<template>
  <div class="profile-page fade-in">
    <!-- Header -->
    <div class="page-header">
      <div class="header-content">
        <p class="title">Os meus dados</p>
        <p class="subtitle">Altere aqui os seus dados pessoais</p>
      </div>
    </div>

    <div class="columns is-variable">
      <!-- Personal Data -->
      <div class="column is-7">
        <div class="card">
          <div class="card-header">
            <div class="card-header-title">
              <i class="fas fa-user mr-2"></i>
              Dados pessoais
            </div>
          </div>
          <div class="card-content">
            <form @submit.prevent="saveProfile">
              <div class="field">
                <label class="label">Nome</label>
                <div class="control">
                  <input class="input" type="text" v-model="profile.name" required>
                </div>
              </div>
              
              <div class="field">
                <label class="label">Morada</label>
                <div class="control">
                  <input class="input" type="text" v-model="profile.address">
                </div>
              </div>
              
              <div class="columns">
                <div class="column is-4">
                  <div class="field">
                    <label class="label">Código Postal</label>
                    <div class="control">
                      <input class="input" type="text" v-model="profile.zipcode" placeholder="0000-000">
                    </div>
                  </div>
                </div>
                <div class="column">
                  <div class="field">
                    <label class="label">Cidade</label>
                    <div class="control">
                      <input class="input" type="text" v-model="profile.city">
                    </div>
                  </div>
                </div>
              </div>
              
              <div class="field">
                <label class="label">Email</label>
                <div class="control">
                  <input class="input" type="email" v-model="profile.email" required>
                </div>
              </div>
              
              <div class="columns">
                <div class="column">
                  <div class="field">
                    <label class="label">Telemóvel</label>
                    <div class="control">
                      <input class="input" type="text" v-model="profile.mobile" placeholder="+351">
                    </div>
                  </div>
                </div>
                <div class="column">
                  <div class="field">
                    <label class="label">Telefone</label>
                    <div class="control">
                      <input class="input" type="text" v-model="profile.phone">
                    </div>
                  </div>
                </div>
              </div>
              
              <button 
                class="button is-success is-medium" 
                :class="{'is-loading':isSaving}"
                :disabled="isSaved"
              >
                <span v-if="isSaved">
                  <i class="fas fa-check mr-2"></i>
                </span>
                {{ saveButtonText }}
              </button>
            </form>
          </div>
        </div>
      </div>
      
      <!-- Password Change -->
      <div class="column is-5">
        <div class="card">
          <div class="card-header">
            <div class="card-header-title">
              <i class="fas fa-lock mr-2"></i>
              Alterar senha
            </div>
          </div>
          <div class="card-content">
            <form @submit.prevent="changePassword">
              <div class="field">
                <label class="label">Senha atual</label>
                <div class="control">
                  <input 
                    class="input" 
                    :class="{'is-danger':password.invalidCurrent}" 
                    type="password" 
                    v-model="password.current" 
                    required
                  >
                </div>
                <p v-if="password.invalidCurrent" class="help is-danger">Senha atual incorreta</p>
              </div>
              
              <div class="field">
                <label class="label">Nova senha</label>
                <div class="control">
                  <input 
                    class="input" 
                    type="password" 
                    v-model="password.new" 
                    minlength="6" 
                    required
                  >
                </div>
                <!-- Password Strength Indicator -->
                <div class="password-strength mt-2" v-if="password.new">
                  <div class="strength-bar">
                    <div class="strength-fill" :class="passwordStrengthClass" :style="{width: passwordStrengthPercent}"></div>
                  </div>
                  <p class="strength-label is-size-7" :class="passwordStrengthClass">{{ passwordStrengthLabel }}</p>
                </div>
              </div>
              
              <div class="field">
                <label class="label">Confirmação nova senha</label>
                <div class="control">
                  <input 
                    class="input" 
                    :class="{'is-danger': password.new !== password.confirmation && password.confirmation}" 
                    type="password" 
                    v-model="password.confirmation" 
                    minlength="6"
                    required
                  >
                </div>
                <p v-if="password.new !== password.confirmation && password.confirmation" class="help is-danger">
                  As senhas não coincidem
                </p>
              </div>
              
              <button 
                class="button is-success is-medium" 
                :class="{'is-loading':isSavingPassword}"
                :disabled="isPasswordSaved || !validNewPassword()"
              >
                <span v-if="isPasswordSaved">
                  <i class="fas fa-check mr-2"></i>
                </span>
                {{ savePasswordButtonText }}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "Profile",
  data() {
    return {
      isSaving: false,
      isSavingPassword: false,
      isSaved: false,
      isPasswordSaved: false,
      password: {
        invalidCurrent: false,
        current: '',
        new: '',
        confirmation: ''
      }
    }
  },
  computed: {
    saveButtonText() {
      return this.isSaved ? 'Guardado!' : 'Guardar alterações';
    },
    savePasswordButtonText() {
      return this.isPasswordSaved ? 'Senha alterada!' : 'Alterar senha';
    },
    profile() {
      return this.$store.getters.profile;
    },
    passwordStrength() {
      const pwd = this.password.new;
      if (!pwd) return 0;
      let strength = 0;
      if (pwd.length >= 6) strength += 25;
      if (pwd.length >= 8) strength += 25;
      if (/[A-Z]/.test(pwd) && /[a-z]/.test(pwd)) strength += 25;
      if (/[0-9]/.test(pwd) || /[^A-Za-z0-9]/.test(pwd)) strength += 25;
      return strength;
    },
    passwordStrengthPercent() {
      return this.passwordStrength + '%';
    },
    passwordStrengthClass() {
      if (this.passwordStrength < 25) return 'weak';
      if (this.passwordStrength < 50) return 'fair';
      if (this.passwordStrength < 75) return 'good';
      return 'strong';
    },
    passwordStrengthLabel() {
      if (this.passwordStrength < 25) return 'Muito fraca';
      if (this.passwordStrength < 50) return 'Fraca';
      if (this.passwordStrength < 75) return 'Boa';
      return 'Forte';
    }
  },
  methods: {
    validNewPassword() {
      return (
        this.password.current.length &&
        this.password.new !== this.password.current &&
        this.password.new === this.password.confirmation &&
        this.password.new.length >= 6
      );
    },
    enableSaveButton() {
      setTimeout(() => {
        this.isSaved = false;
      }, 3000);
    },
    enablePasswordSaveButton() {
      setTimeout(() => {
        this.isPasswordSaved = false;
      }, 3000);
    },
    saveProfile() {
      this.isSaving = true;
      this.$store.dispatch('saveProfile')
        .then(() => {
          this.isSaved = true;
          this.isSaving = false;
          this.enableSaveButton();
        });
    },
    clearPasswords() {
      this.password = {
        invalidCurrent: false,
        current: '',
        new: '',
        confirmation: ''
      };
    },
    changePassword() {
      if (this.validNewPassword()) {
        this.isSavingPassword = true;
        this.$store.dispatch('changePassword', {
          newPassword: this.password.new,
          oldPassword: this.password.current
        })
        .then(() => {
          this.password.invalidCurrent = false;
          this.isPasswordSaved = true;
          this.isSavingPassword = false;
          this.enablePasswordSaveButton();
          this.clearPasswords();
        })
        .catch(() => {
          this.isSavingPassword = false;
          this.password.invalidCurrent = true;
        });
      }
    }
  },
  mounted() {
    this.$store.dispatch('fetchProfile');
  }
}
</script>

<style scoped>
.profile-page {
  max-width: 1200px;
}

.page-header {
  margin-bottom: 32px;
}

.page-header .title {
  font-size: 1.75rem;
  font-weight: 700;
  margin-bottom: 16px;
}

.page-header .subtitle {
  color: var(--text-secondary);
  margin-top: 20px;
}

.card-header-title {
  display: flex;
  align-items: center;
  font-weight: 600;
  color: var(--text-primary);
  background: var(--surface-elevated);
}

.card-header-title i {
  color: var(--primary);
}

.field {
  margin-bottom: 1.25rem;
}

.input {
  height: 44px;
}

.input:focus {
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(7, 85, 160, 0.1);
}

.button.is-success {
  background: linear-gradient(135deg, var(--success) 0%, #059669 100%);
  border: none;
}

.button.is-success:hover {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

/* Password Strength */
.password-strength {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.strength-bar {
  height: 4px;
  background: #E2E8F0;
  border-radius: 2px;
  overflow: hidden;
}

.strength-fill {
  height: 100%;
  border-radius: 2px;
  transition: all var(--transition-base);
}

.strength-fill.weak { background: var(--danger); width: 25%; }
.strength-fill.fair { background: var(--warning); width: 50%; }
.strength-fill.good { background: #22C55E; width: 75%; }
.strength-fill.strong { background: var(--success); width: 100%; }

.strength-label.weak { color: var(--danger); }
.strength-label.fair { color: var(--warning); }
.strength-label.good { color: #22C55E; }
.strength-label.strong { color: var(--success); }

@media only screen and (max-width: 768px) {
  .columns {
    flex-direction: column;
  }
  
  .column {
    width: 100%;
    margin-bottom: 24px;
  }
}
</style>
